import { CommonSectionIds } from '../common.models';

export const TritonSectionIds = {
    BasicInfo: CommonSectionIds.BasicInfo,
    ConfigureAlarms: CommonSectionIds.ConfigureAlarms,
    DataDelivery: CommonSectionIds.DataDelivery,
    Advanced: CommonSectionIds.Advanced,
    DialogActions: CommonSectionIds.DialogActions,

    TritonInfo: 'triton-info-section',
    Mp2BasicInfo: 'mp2-basic-info-section',
    Mp2Alarms: 'mp2-alarms-config-section',

    Flow1: 'flow1-section',
    Flow2: 'flow2-section',

    Sampler: 'sampler-info-section',
};

export const TritonSensors = {
    AvGated: 'AvGated',
    LongRange: 'LongRange',
    PeakCombo: 'PeakCombo',
    SmartDepth: 'SmartDepth',
    SurfaceCombo: 'SurfaceCombo',
};

export const Mp1SensorIds = {
    [TritonSensors.AvGated]: 'avgated-sensor-section',
    [TritonSensors.LongRange]: 'long-range-sensor-section',
    [TritonSensors.PeakCombo]: 'peak-combo-sensor-section',
    [TritonSensors.SmartDepth]: 'smart-depth-sensor-section',
    [TritonSensors.SurfaceCombo]: 'surface-combo-sensor-section',
};

export const Mp2SensorIds = {
    [TritonSensors.AvGated]: 'mp2-avgated-sensor-section',
    [TritonSensors.LongRange]: 'mp2-long-range-sensor-section',
    [TritonSensors.PeakCombo]: 'mp2-peak-combo-sensor-section',
    [TritonSensors.SmartDepth]: 'mp2-smart-depth-sensor-section',
    [TritonSensors.SurfaceCombo]: 'mp2-surface-combo-sensor-section',
};

export function getSensorFieldId(baseFieldId: string, mp: 0 | 1): string {
    return mp === 0 ? baseFieldId : `mp2-${baseFieldId}`;
}

export function getSensorKeyById(id: string): string {
    switch (id) {
        case Mp1SensorIds[TritonSensors.AvGated]:
        case Mp2SensorIds[TritonSensors.AvGated]:
            return TritonSensors.AvGated;
        case Mp1SensorIds[TritonSensors.LongRange]:
        case Mp2SensorIds[TritonSensors.LongRange]:
            return TritonSensors.LongRange;
        case Mp1SensorIds[TritonSensors.PeakCombo]:
        case Mp2SensorIds[TritonSensors.PeakCombo]:
            return TritonSensors.PeakCombo;
        case Mp1SensorIds[TritonSensors.SmartDepth]:
        case Mp2SensorIds[TritonSensors.SmartDepth]:
            return TritonSensors.SmartDepth;
        case Mp1SensorIds[TritonSensors.SurfaceCombo]:
        case Mp2SensorIds[TritonSensors.SurfaceCombo]:
            return TritonSensors.SurfaceCombo;
        default:
            return null;
    }
}

export const Mp1SensorTitles = {
    [TritonSensors.AvGated]: 'LOCATION_CARD.AV_GATED_SENSOR_NAME_MP1',
    [TritonSensors.LongRange]: 'LOCATION_CARD.LONG_RANGE_SENSOR_NAME_MP1',
    [TritonSensors.PeakCombo]: 'LOCATION_CARD.PEAK_COMBO_SENSOR_NAME_MP1',
    [TritonSensors.SmartDepth]: 'LOCATION_CARD.SMART_DEPTH_SENSOR_NAME_MP1',
    [TritonSensors.SurfaceCombo]: 'LOCATION_CARD.SURFACE_COMBO_SENSOR_NAME_MP1',
};

export const Mp2SensorTitles = {
    [TritonSensors.AvGated]: 'LOCATION_CARD.AV_GATED_SENSOR_NAME_MP2',
    [TritonSensors.LongRange]: 'LOCATION_CARD.LONG_RANGE_SENSOR_NAME_MP2',
    [TritonSensors.PeakCombo]: 'LOCATION_CARD.PEAK_COMBO_SENSOR_NAME_MP2',
    [TritonSensors.SmartDepth]: 'LOCATION_CARD.SMART_DEPTH_SENSOR_NAME_MP2',
    [TritonSensors.SurfaceCombo]: 'LOCATION_CARD.SURFACE_COMBO_SENSOR_NAME_MP2',
};

export enum TritonInfoFieldId {
    StoreTemperature = 'echo-info-storetemperature',
    BatteryVoltage = 'echo-info-batteryvoltage',
    
    // Sensor 1 fields
    Sensor1 = 'triton-info-sensor1',
    MP1 = 'triton-info-mp1',
    Channel1 = 'triton-info-channel1',
    Silt1 = 'triton-info-silt1',
    SiltEditButton1 = 'triton-info-siltEditButton1',

    // Sensor 2 fields
    Sensor2 = 'triton-info-sensor2',
    MP2 = 'triton-info-mp2',
    Channel2 = 'triton-info-channel2',
    Silt2 = 'triton-info-silt2',
    SiltEditButton2 = 'triton-info-siltEditButton2',

    // Sensor 1 Preferences
    DepthSensor1 = 'triton-info-depthSensor1',
    VelocitySensor1 = 'triton-info-velocitySensor1',

    // Sensor 2 Preferences
    DepthSensor2 = 'triton-info-depthSensor2',
    VelocitySensor2 = 'triton-info-velocitySensor2',

    // titles
    SensorPreferencesTitle = 'triton-info-sensor-preferences-title-1',
    SensorPreferencesTitle2 = 'triton-info-sensor-preferences-title-2',
    SensorPreferenceDivider = 'triton-info-sensor-preference-divider',

    ComportDivider = 'triton-info-comport-divider',

    // Commport
    ComPort = 'triton-info-comPort',
    TiltAlarmThreshold = 'triton-info-tiltAlarmThreshold',

    // Same sensor error
    SameSensorError = 'triton-info-sameSensorError',
}

export enum Mp2ConfigureAlarmsFieldId {
    LowLevelEnable = 'mp2-alarms-lowLevelEnable',
    LowLevelThreshold = 'mp2-alarms-lowLevelThreshold',
    FullPipeEnable = 'mp2-alarms-fullPipeEnable',
    FullPipeThreshold = 'mp2-alarms-fullPipethreshold',
    HighLevelEnable = 'mp2-alarms-highLevelEnable',
    HighLevelThreshold = 'mp2-alarms-highLevelThreshold',
    HighHighEnable = 'mp2-alarms-highHighEnable',
    HighHighThreshold = 'mp2-alarms-highHighThreshold',
    HighFlowEnable = 'mp2-alarms-highFlowEnable',
    HighFlowThreshold = 'mp2-alarms-highFlowThreshold',
    OverflowEnable = 'mp2-alarms-overflowEnable',
    BatteryLowEnable = 'mp2-alarms-batteryLowEnable',
    BatteryLowThreshold = 'mp2-alarms-batteryLowThreshold',
    FlowLossAlarm = 'mp2-alarms-flowLossAlarm',
    FlowLossType = 'mp2-alarms-flowLossType',
    Percent = 'mp2-alarms-percent',
    Sensitivity = 'mp2-alarms-sensitivity',
    ClearPatternHistory = 'mp2-alarms-clearPatternHistory',
}

export enum Mp2BasicInfoFieldId {
    StatusSection = 'mp2-basic-info-status-section',
    QFinalEntityID = 'mp2-basic-info-qFinalEntityID',
    AssignedRainGauge = 'mp2-basic-info-assignedRainGauge',
    Status = 'mp2-basic-info-status',
    MaintenanceInterval = 'mp2-basic-info-maintenanceInterval',
    MaintenanceEnd = 'mp2-basic-info-maintenanceEnd',
    InstallationType = 'mp2-basic-info-installationType',
    InstallationShapeType = 'mp2-basic-info-installationShapeType',
    InstallationDate = 'mp2-basic-info-installationDate',
    CustomPropertiesSection = 'mp2-basic-info-custom-properties-section',
    CustomPropertyKey = 'mp2-basic-info-custom-property-key',
    CustomPropertyValue = 'mp2-basic-info-custom-property-value',
    InstallationRow = 'mp2-installation-row',
}

export enum Mp1FlowFieldId {
    Parameters = 'mp1-flow-parameters',
}

export enum Mp2FlowFieldId {
    Parameters = 'mp2-flow-parameters',
}

export const TRITON_MONITOR_CONFIGURATION_CONSTANTS = {
    ftpdeliverynormal: 0,
    ftpdeliveryfast: 0,
    ftpfolder: '',
    ftppassword: '',
    ftpusername: '',
    ftpserver: '',
    ftpmode: 'Active',
    ftpformat: 'ADS',
    storetemperature: false,
    storebattery: 1,
};
